// components/ModalBiometria.js
// Modal para captura de selfie y validación biométrica

import React, { useState, useRef, useCallback, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
  Spinner,
  Progress,
} from "reactstrap"

import { validarBiometria } from "api/biometriaApi"

// Estados del proceso
const ESTADOS = {
  INICIAL: "INICIAL",
  SOLICITANDO_CAMARA: "SOLICITANDO_CAMARA",
  CAMARA_ACTIVA: "CAMARA_ACTIVA",
  PREVIEW_CAPTURA: "PREVIEW_CAPTURA",
  VALIDANDO: "VALIDANDO",
  EXITOSO: "EXITOSO",
  ERROR: "ERROR",
  ERROR_CAMARA: "ERROR_CAMARA",
}

const ModalBiometria = ({ isOpen, toggle, firmante, cuit, onResultado }) => {
  const [estado, setEstado] = useState(ESTADOS.INICIAL)
  const [error, setError] = useState(null)
  const [resultado, setResultado] = useState(null)
  const [imagenCapturada, setImagenCapturada] = useState(null)
  const [camaraLista, setCamaraLista] = useState(false)

  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)

  // Función para detener la cámara de forma segura
  const detenerCamara = useCallback(() => {
    console.log("[Biometria] Deteniendo cámara...")

    // Primero quitar el srcObject del video
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.srcObject = null
    }

    // Luego detener todos los tracks del stream
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks()
      tracks.forEach(track => {
        console.log("[Biometria] Deteniendo track:", track.kind, track.label)
        track.stop()
      })
      streamRef.current = null
    }

    setCamaraLista(false)
    console.log("[Biometria] Cámara detenida")
  }, [])

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      console.log("[Biometria] Componente desmontando - limpiando recursos")
      detenerCamara()
    }
  }, [detenerCamara])

  // Reset al abrir/cerrar modal
  useEffect(() => {
    if (isOpen) {
      console.log("[Biometria] Modal abierto - reseteando estado")
      setEstado(ESTADOS.INICIAL)
      setError(null)
      setResultado(null)
      setImagenCapturada(null)
      setCamaraLista(false)
    } else {
      console.log("[Biometria] Modal cerrado - deteniendo cámara")
      detenerCamara()
    }
  }, [isOpen, detenerCamara])

  // Iniciar cámara
  const iniciarCamara = useCallback(async () => {
    // Primero asegurarse de que no hay un stream previo
    detenerCamara()

    setError(null)
    setCamaraLista(false)

    // Verificar contexto seguro
    const isSecureContext =
      window.isSecureContext ||
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"

    if (!isSecureContext) {
      setEstado(ESTADOS.ERROR_CAMARA)
      setError(
        "La cámara solo está disponible en conexiones seguras (HTTPS). Contacte al administrador para habilitar HTTPS en el servidor.",
      )
      return
    }

    // Verificar soporte del navegador
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setEstado(ESTADOS.ERROR_CAMARA)
      setError(
        "Su navegador no soporta acceso a la cámara. Por favor use un navegador moderno (Chrome, Firefox, Edge).",
      )
      return
    }

    // IMPORTANTE: Cambiar estado ANTES de solicitar el stream
    // para que el <video> element se renderice
    setEstado(ESTADOS.SOLICITANDO_CAMARA)

    // Pequeño delay para asegurar que React renderice el video element
    await new Promise(resolve => setTimeout(resolve, 100))

    try {
      console.log("[Biometria] Solicitando acceso a cámara...")

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user",
        },
        audio: false,
      })

      console.log("[Biometria] Stream obtenido:", stream.id)

      // Guardar referencia al stream
      streamRef.current = stream

      // Verificar que videoRef esté disponible
      if (!videoRef.current) {
        console.error("[Biometria] videoRef no disponible después de delay")
        throw new Error("Elemento de video no disponible")
      }

      console.log("[Biometria] Asignando stream al video element")
      videoRef.current.srcObject = stream

      // Esperar a que el video esté listo
      videoRef.current.onloadedmetadata = () => {
        console.log("[Biometria] Video metadata cargada")
        if (videoRef.current) {
          videoRef.current
            .play()
            .then(() => {
              console.log("[Biometria] Video reproduciendo")
              setCamaraLista(true)
              setEstado(ESTADOS.CAMARA_ACTIVA)
            })
            .catch(err => {
              console.error("[Biometria] Error reproduciendo video:", err)
              setEstado(ESTADOS.ERROR_CAMARA)
              setError("Error al iniciar la vista previa de la cámara")
              detenerCamara()
            })
        }
      }

      videoRef.current.onerror = err => {
        console.error("[Biometria] Error en video element:", err)
        setEstado(ESTADOS.ERROR_CAMARA)
        setError("Error en el elemento de video")
        detenerCamara()
      }
    } catch (err) {
      console.error("[Biometria] Error accediendo a la cámara:", err)
      setEstado(ESTADOS.ERROR_CAMARA)
      detenerCamara()

      if (err.name === "NotAllowedError") {
        setError(
          "Permiso de cámara denegado. Por favor, permita el acceso a la cámara en la configuración del navegador.",
        )
      } else if (err.name === "NotFoundError") {
        setError("No se encontró ninguna cámara en el dispositivo.")
      } else if (err.name === "NotReadableError") {
        setError(
          "La cámara está siendo usada por otra aplicación. Cierre otras aplicaciones que usen la cámara e intente nuevamente.",
        )
      } else if (err.name === "OverconstrainedError") {
        setError("No se encontró una cámara compatible con los requisitos.")
      } else {
        setError("Error al acceder a la cámara: " + err.message)
      }
    }
  }, [detenerCamara])

  // Capturar imagen
  const capturarImagen = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      console.error("[Biometria] Referencias no disponibles para captura")
      return
    }

    const video = videoRef.current

    // Verificar que el video tenga dimensiones válidas
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.error("[Biometria] Video sin dimensiones válidas")
      setError(
        "La cámara no está lista. Espere un momento e intente nuevamente.",
      )
      return
    }

    console.log(
      "[Biometria] Capturando imagen:",
      video.videoWidth,
      "x",
      video.videoHeight,
    )

    const canvas = canvasRef.current
    const context = canvas.getContext("2d")

    // Configurar canvas
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Aplicar espejo horizontal
    context.translate(canvas.width, 0)
    context.scale(-1, 1)

    // Dibujar frame
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Resetear transformación
    context.setTransform(1, 0, 0, 1, 0, 0)

    // Obtener imagen como base64
    const imagenBase64 = canvas.toDataURL("image/jpeg", 0.9)

    console.log("[Biometria] Imagen capturada, deteniendo cámara")

    // IMPORTANTE: Primero detener cámara, luego actualizar estado
    detenerCamara()

    // Actualizar estado DESPUÉS de detener la cámara
    setImagenCapturada(imagenBase64)
    setEstado(ESTADOS.PREVIEW_CAPTURA)

    console.log("[Biometria] Estado actualizado a PREVIEW_CAPTURA con imagen")
  }, [detenerCamara])

  // Reintentar captura
  const reintentarCaptura = useCallback(() => {
    setImagenCapturada(null)
    setError(null)
    setResultado(null)
    iniciarCamara()
  }, [iniciarCamara])

  // Enviar para validación
  const enviarValidacion = useCallback(async () => {
    if (!imagenCapturada || !firmante) return

    setEstado(ESTADOS.VALIDANDO)
    setError(null)

    try {
      const response = await validarBiometria({
        numeroDocumento: firmante.numeroDocumento,
        tipoDocumento: firmante.tipoDocumento || "DNI",
        sexo: firmante.sexo || "M",
        nombre: firmante.nombre,
        apellido: firmante.apellido,
        imagenBase64: imagenCapturada,
        cuit: cuit,
      })

      setResultado(response)

      if (response.exitoso) {
        setEstado(ESTADOS.EXITOSO)
        setTimeout(() => {
          onResultado?.(firmante.numeroDocumento, "APROBADA", response)
          toggle()
        }, 2000)
      } else {
        setEstado(ESTADOS.ERROR)
        setError(response.mensaje || "La validación biométrica no fue exitosa")
      }
    } catch (err) {
      console.error("[Biometria] Error en validación:", err)
      setEstado(ESTADOS.ERROR)
      setError("Error de conexión al validar biometría")
    }
  }, [imagenCapturada, firmante, cuit, onResultado, toggle])

  // Cerrar modal
  const handleClose = useCallback(() => {
    console.log("[Biometria] Cerrando modal")
    detenerCamara()

    if (estado !== ESTADOS.EXITOSO) {
      onResultado?.(firmante?.numeroDocumento, "PENDIENTE", null)
    }

    toggle()
  }, [detenerCamara, estado, firmante, onResultado, toggle])

  // Render contenido según estado
  const renderContenido = () => {
    switch (estado) {
      case ESTADOS.INICIAL:
        return (
          <div className="text-center py-4">
            <i
              className="bx bx-camera"
              style={{ fontSize: 64, color: "#6c757d" }}
            />
            <h5 className="mt-3">Validación Biométrica</h5>
            <p className="text-muted">
              Se tomará una foto de su rostro para validar su identidad contra
              Renaper.
            </p>
            <Button color="primary" size="lg" onClick={iniciarCamara}>
              <i className="bx bx-camera me-2" />
              Iniciar Cámara
            </Button>
          </div>
        )

      case ESTADOS.SOLICITANDO_CAMARA:
      case ESTADOS.CAMARA_ACTIVA:
        return (
          <div className="text-center">
            <div
              className="position-relative mx-auto"
              style={{
                maxWidth: 500,
                height: 400,
                borderRadius: 12,
                overflow: "hidden",
                backgroundColor: "#000",
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  transform: "scaleX(-1)",
                }}
              />

              {/* Indicador de carga mientras la cámara inicia */}
              {(estado === ESTADOS.SOLICITANDO_CAMARA || !camaraLista) && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
                >
                  <div className="text-center text-white">
                    <Spinner color="light" />
                    <p className="mt-2 mb-0">
                      {estado === ESTADOS.SOLICITANDO_CAMARA
                        ? "Solicitando acceso a la cámara..."
                        : "Iniciando cámara..."}
                    </p>
                  </div>
                </div>
              )}

              {/* Overlay con guía de rostro */}
              {camaraLista && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{ pointerEvents: "none" }}
                >
                  <div
                    style={{
                      width: 180,
                      height: 240,
                      border: "3px dashed rgba(255,255,255,0.6)",
                      borderRadius: "50%",
                      boxShadow: "0 0 0 9999px rgba(0,0,0,0.3)",
                    }}
                  />
                </div>
              )}
            </div>

            <p className="mt-3 text-muted">
              <i className="bx bx-info-circle me-1" />
              Ubique su rostro dentro del óvalo y presione capturar
            </p>

            <Button
              color="primary"
              size="lg"
              onClick={capturarImagen}
              disabled={!camaraLista || estado === ESTADOS.SOLICITANDO_CAMARA}
            >
              <i className="bx bx-camera me-2" />
              {camaraLista ? "Capturar" : "Esperando cámara..."}
            </Button>
          </div>
        )

      case ESTADOS.PREVIEW_CAPTURA:
        return (
          <div className="text-center py-4">
            <h5 className="mb-3">Vista previa de la captura</h5>
            {imagenCapturada && (
              <img
                src={imagenCapturada}
                alt="Captura"
                style={{
                  maxWidth: 400,
                  maxHeight: 350,
                  borderRadius: 12,
                  border: "3px solid #28a745",
                }}
              />
            )}
            <p className="mt-3 text-muted">
              <i className="bx bx-check-circle text-success me-1" />
              ¿La imagen se ve correctamente? Presione Confirmar y Validar para
              continuar.
            </p>
            <div className="mt-3">
              <Button
                color="outline-secondary"
                onClick={reintentarCaptura}
                className="me-2"
              >
                <i className="bx bx-refresh me-1" />
                Tomar otra foto
              </Button>
            </div>
          </div>
        )

      case ESTADOS.VALIDANDO:
        return (
          <div className="text-center py-4">
            {imagenCapturada && (
              <img
                src={imagenCapturada}
                alt="Captura"
                style={{
                  maxWidth: 300,
                  borderRadius: 12,
                  marginBottom: 20,
                  opacity: 0.7,
                }}
              />
            )}
            <div className="mb-3">
              <Spinner color="primary" style={{ width: 48, height: 48 }} />
            </div>
            <h5>Validando identidad...</h5>
            <p className="text-muted">Conectando con Renaper</p>
            <Progress
              animated
              value={100}
              className="mx-auto"
              style={{ maxWidth: 300 }}
            />
          </div>
        )

      case ESTADOS.EXITOSO:
        return (
          <div className="text-center py-4">
            <div
              className="rounded-circle bg-success d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 80, height: 80 }}
            >
              <i className="bx bx-check text-white" style={{ fontSize: 48 }} />
            </div>
            <h4 className="text-success">¡Validación Exitosa!</h4>
            <p className="text-muted">
              Identidad verificada correctamente
              {resultado?.scoreMatch && (
                <span className="d-block mt-1">
                  Score de coincidencia:{" "}
                  <strong>{resultado.scoreMatch.toFixed(1)}%</strong>
                </span>
              )}
            </p>
          </div>
        )

      case ESTADOS.ERROR:
      case ESTADOS.ERROR_CAMARA:
        return (
          <div className="text-center py-4">
            <div
              className="rounded-circle bg-danger d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 80, height: 80 }}
            >
              <i className="bx bx-x text-white" style={{ fontSize: 48 }} />
            </div>
            <h4 className="text-danger">
              {estado === ESTADOS.ERROR_CAMARA
                ? "Error de Cámara"
                : "Validación Fallida"}
            </h4>
            <Alert color="danger" className="mt-3 text-start">
              {error || "Ocurrió un error durante la validación"}
            </Alert>

            {imagenCapturada && (
              <img
                src={imagenCapturada}
                alt="Captura fallida"
                style={{
                  maxWidth: 200,
                  borderRadius: 12,
                  marginBottom: 20,
                  opacity: 0.5,
                }}
              />
            )}

            <div className="mt-3">
              <Button color="primary" onClick={reintentarCaptura}>
                <i className="bx bx-refresh me-2" />
                Reintentar
              </Button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="lg"
      centered
      backdrop="static"
    >
      <ModalHeader toggle={handleClose}>
        <i className="bx bx-user-check me-2" />
        Validación Biométrica
        {firmante && (
          <small className="text-muted ms-2">
            - {firmante.nombre} {firmante.apellido}
          </small>
        )}
      </ModalHeader>

      <ModalBody>
        {/* Canvas oculto para captura */}
        <canvas ref={canvasRef} style={{ display: "none" }} />

        {/* Info del firmante */}
        {firmante && estado === ESTADOS.INICIAL && (
          <Alert color="info" className="mb-4">
            <div className="d-flex align-items-center">
              <i className="bx bx-id-card me-3" style={{ fontSize: 32 }} />
              <div>
                <strong>
                  {firmante.nombre} {firmante.apellido}
                </strong>
                <div className="small">
                  {firmante.tipoDocumento || "DNI"}: {firmante.numeroDocumento}
                </div>
              </div>
            </div>
          </Alert>
        )}

        {renderContenido()}
      </ModalBody>

      <ModalFooter>
        {estado === ESTADOS.PREVIEW_CAPTURA && imagenCapturada && (
          <Button color="success" onClick={enviarValidacion}>
            <i className="bx bx-check me-2" />
            Confirmar y Validar
          </Button>
        )}

        {![ESTADOS.EXITOSO, ESTADOS.VALIDANDO].includes(estado) && (
          <Button color="secondary" onClick={handleClose}>
            Cancelar
          </Button>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default ModalBiometria
